import { Box } from "@mui/material";
import { FC, type ForwardedRef, forwardRef, type Ref } from "react";
import { useTranslation } from "react-i18next";
import AmountsAndStatus, { SelfSchedulingStatus } from "./AmountsAndStatus";

type Props = {
	signupShiftsCurrentAmount?: number;
	signupShiftsTargetAmount?: number;
	weekendsCurrentAmount?: number;
	weekendsTargetAmount?: number;
	rdosCurrentAmount?: number;
	rdosMaxAmount?: number;
	ref?: Ref<HTMLDivElement>;
};

/**
 * SelfSchedulingMetrics component displays the current and maximum amounts of
 * @param signupShiftsCurrentAmount - Current amount of signup shifts
 * @param rdosCurrentAmount - Current amount of RDOs (Rest Days Off)
 * @param rdosMaxAmount - Maximum amount of RDOs allowed
 * @param signupShiftsTargetAmount - Target amount of signup shifts allowed
 * @param weekendsCurrentAmount - Current amount of weekends worked
 * @param weekendsTargetAmount - Target amount of weekends allowed
 * @param ref - Optional ref to attach to the component
 */
const SelfSchedulingMetrics = forwardRef<HTMLDivElement, Props>(
	(
		{
			signupShiftsCurrentAmount = 0,
			rdosCurrentAmount = 0,
			rdosMaxAmount = 6,
			signupShiftsTargetAmount = 18,
			weekendsCurrentAmount = 0,
			weekendsTargetAmount = 4,
		},
		ref: ForwardedRef<HTMLDivElement>,
	) => {
		const { t } = useTranslation();
		return (
			<Box
				ref={ref}
				sx={{
					display: "flex",
					alignItems: "center",

					width: {
						xs: "100%", // Mobile (0px and up)
						sm: "fit-content", // From 600px and up
					},

					gap: {
						xs: 0,
						sm: 10,
					},

					justifyContent: {
						xs: "space-around",
						sm: "center",
					},
				}}
			>
				<AmountsAndStatus
					label={t("signed.up")}
					status={
						signupShiftsCurrentAmount >= signupShiftsTargetAmount && signupShiftsTargetAmount > 0 ? SelfSchedulingStatus.Done : SelfSchedulingStatus.Missing
					}
					currentAmount={signupShiftsCurrentAmount}
					maxAmount={signupShiftsTargetAmount}
				/>
				<AmountsAndStatus
					label={t("weekends")}
					status={weekendsCurrentAmount >= weekendsTargetAmount && weekendsTargetAmount > 0 ? SelfSchedulingStatus.Done : SelfSchedulingStatus.Missing}
					currentAmount={weekendsCurrentAmount}
					maxAmount={weekendsTargetAmount}
				/>
				<AmountsAndStatus
					label={t("rdos")}
					status={
						rdosCurrentAmount === rdosMaxAmount && rdosMaxAmount > 0
							? SelfSchedulingStatus.Done
							: rdosCurrentAmount > rdosMaxAmount
								? SelfSchedulingStatus.OverLimit
								: SelfSchedulingStatus.Optional
					}
					currentAmount={rdosCurrentAmount}
					maxAmount={rdosMaxAmount}
				/>
			</Box>
		);
	},
);

// Give the component a name (for debugging tools)
SelfSchedulingMetrics.displayName = "SelfSchedulingMetrics";

export default SelfSchedulingMetrics;
